import type { FC, FormEvent, ReactNode } from 'react'
import type { FieldErrors } from '@/types'
import type { ContactField, ContactValues } from '@/lib/forms'

import { useState } from 'react'
import { MailCheck, Send } from 'lucide-react'
import { CONTACT_TOPICS, EMPTY_CONTACT, validateContact } from '@/lib/forms'
import { accentButton, ghostButton } from '@/components/generals/button-styles'
import {
    FormField,
    Honeypot,
    describedBy,
    inputClass
} from '@/components/generals/form-field'

type Status = 'idle' | 'sending' | 'error' | 'done'

const ContactForm: FC = (): ReactNode => {
    const [values, setValues] = useState<ContactValues>(EMPTY_CONTACT)
    const [errors, setErrors] = useState<FieldErrors<ContactField>>({})
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')
    const [sentTo, setSentTo] = useState('')

    const update = (field: keyof ContactValues, value: string): void => {
        setValues(current => ({ ...current, [field]: value }))
        if (field in errors) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const { errors: found } = validateContact(values)
        if (Object.keys(found).length > 0) {
            setErrors(found)
            setStatus('error')
            setMessage('Please check the highlighted fields.')
            return
        }

        setStatus('sending')
        setMessage('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })
            const result = await response.json().catch(() => ({}))

            if (!response.ok || !result.ok) {
                setErrors(result.fields ?? {})
                setStatus('error')
                setMessage(result.error ?? "We couldn't send your message just now. Please try again in a moment.")
                return
            }

            setSentTo(values.email)
            setValues(EMPTY_CONTACT)
            setStatus('done')
        } catch {
            setStatus('error')
            setMessage('Your connection dropped before the message reached us. Please try again.')
        }
    }

    if (status === 'done') {
        return (
            <div role='status' className='animate-in fade-in slide-in-from-bottom-2 duration-700'>
                <span className='inline-flex size-12 items-center justify-center rounded-full bg-bark text-paper'>
                    <MailCheck className='size-5' strokeWidth={1.75} />
                </span>
                <h3 className='mt-6 font-heading text-[2rem] font-light leading-tight text-bark'>
                    Thank you, your note is with us.
                </h3>
                <p className='mt-4 text-[1.02rem] leading-relaxed text-bark-soft'>
                    We reply within one working day, to <strong className='font-semibold text-bark'>{sentTo}</strong>. If it is the weekend in Samarkand, give us until Monday evening.
                </p>
                <button type='button' onClick={() => setStatus('idle')} className={`${ghostButton} mt-8`}>
                    Write another message
                </button>
            </div>
        )
    }

    return (
        <form noValidate onSubmit={submit} className='relative space-y-5' aria-describedby={message ? 'contact-status' : undefined}>
            <Honeypot value={values.company} onChange={value => update('company', value)} />

            <div className='grid gap-5 sm:grid-cols-2'>
                <FormField id='contact-name' label='Your name' error={errors.fullName}>
                    <input
                        id='contact-name'
                        type='text'
                        autoComplete='name'
                        value={values.fullName}
                        onChange={event => update('fullName', event.target.value)}
                        aria-invalid={Boolean(errors.fullName)}
                        aria-describedby={describedBy('contact-name', errors.fullName)}
                        className={inputClass}
                    />
                </FormField>

                <FormField id='contact-email' label='Email' error={errors.email}>
                    <input
                        id='contact-email'
                        type='email'
                        autoComplete='email'
                        inputMode='email'
                        value={values.email}
                        onChange={event => update('email', event.target.value)}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={describedBy('contact-email', errors.email)}
                        className={inputClass}
                    />
                </FormField>
            </div>

            <FormField id='contact-topic' label='What is it about?' error={errors.topic}>
                <select
                    id='contact-topic'
                    value={values.topic}
                    onChange={event => update('topic', event.target.value)}
                    aria-invalid={Boolean(errors.topic)}
                    aria-describedby={describedBy('contact-topic', errors.topic)}
                    className={inputClass}
                >
                    {CONTACT_TOPICS.map(topic => (
                        <option key={topic} value={topic}>{topic}</option>
                    ))}
                </select>
            </FormField>

            <FormField id='contact-message' label='Your message' error={errors.message}>
                <textarea
                    id='contact-message'
                    rows={6}
                    value={values.message}
                    onChange={event => update('message', event.target.value)}
                    placeholder='Tell us what you are making, how much paper you need, or when you will be in Samarkand.'
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={describedBy('contact-message', errors.message)}
                    className={`${inputClass} resize-y`}
                />
            </FormField>

            <div className='flex flex-col gap-4 pt-2 sm:flex-row sm:items-center sm:justify-between'>
                <p className='text-sm text-bark-soft'>We answer every message ourselves.</p>
                <button type='submit' disabled={status === 'sending'} className={accentButton}>
                    <Send className='size-4' strokeWidth={1.75} />
                    {status === 'sending' ? 'Sending' : 'Send message'}
                </button>
            </div>

            <p id='contact-status' role='status' className='min-h-5 text-sm text-pomegranate'>
                {status === 'error' ? message : ''}
            </p>
        </form>
    )
}

export default ContactForm
